import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Button,
  Container,
  CircularProgress,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CycleSidePanel from './CycleSidePanel';
import CycleNotesModule from './CycleNotesModule';
import CycleFilesModule from './CycleFilesModule';
import { useUser } from './UserContext';
import { formatDate } from './utils/dateUtils';
import { Divider } from '@mui/material';

// --- Helper Components ---

const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) {
    return path;
  }
  return path; // The backend now provides the full relative path with a leading slash
};

const StatusChip = ({ status, cicloStatus }) => {
  const displayStatus = cicloStatus && cicloStatus !== 'activo' 
    ? cicloStatus.charAt(0).toUpperCase() + cicloStatus.slice(1) 
    : status;
  
  const statusStyles = {
    'Borrador': { label: 'Borrador', color: 'default' },
    'Firmado Supervisión': { label: 'Firmado 1/2', color: 'warning' },
    'Firmado Dirección General': { label: 'Firmado 1/2', color: 'warning' },
    'Aprobada': { label: 'Aprobada', color: 'success' },
    'Cancelado': { label: 'Cancelado', style: { backgroundColor: '#000000', color: 'white' } },
    'Terminado': { label: 'Terminado', style: { backgroundColor: '#FFC107', color: 'black' } },
  };

  const styleInfo = statusStyles[displayStatus] || { label: displayStatus, color: 'default' };

  return <Chip label={styleInfo.label} color={styleInfo.color} style={styleInfo.style} />;
};

const FamilyTree = ({ oc }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
    {oc.requisicion_id && (
      <>
        <MuiLink component={Link} to={`/requisiciones/${oc.requisicion_id}`} sx={{ textDecoration: 'none' }}>
          <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
            <Typography variant="subtitle1">
              {oc.requisicion?.doc_numb || `REQ-${oc.requisicion_id.substring(0, 6)}`}
            </Typography>
          </Paper>
        </MuiLink>
        <ArrowForwardIosIcon sx={{ mx: 1 }} />
      </>
    )}
    {oc.solicitud_cotizacion_id && (
      <>
        <MuiLink component={Link} to={`/solicitudes_cotizacion/${oc.solicitud_cotizacion_id}`} sx={{ textDecoration: 'none' }}>
          <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
            <Typography variant="subtitle1">
              {oc.solicitud_cotizacion?.doc_numb || `RFQ-${oc.solicitud_cotizacion_id.substring(0, 6)}`}
            </Typography>
          </Paper>
        </MuiLink>
        <ArrowForwardIosIcon sx={{ mx: 1 }} />
      </>
    )}
    <Paper elevation={3} sx={{ p: 1, bgcolor: 'primary.main', color: 'white' }}>
      <Typography variant="subtitle1">{oc.doc_numb || `OC-${oc.id.substring(0, 6)}`}</Typography>
    </Paper>
  </Box>
);

// --- Main Component ---

const OrdenCompraDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser(); // Get user from context
  const [ordenCompra, setOrdenCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [openFinishDialog, setOpenFinishDialog] = useState(false);
  const [openSupervisionDialog, setOpenSupervisionDialog] = useState(false);
  const [openDireccionGeneralDialog, setOpenDireccionGeneralDialog] = useState(false);

  const [htmlContent, setHtmlContent] = useState('');

  const fetchOrdenCompra = () => {
    setLoading(true);
    axios.get(`/api/ordenes_compra/${id}`)
      .then(res => {
        setOrdenCompra(res.data);
        // Process HTML content to make image URLs absolute
        let processedHtml = res.data.html_content || '';
        if (processedHtml) {
          // Convert relative image URLs to absolute URLs
          // Use axios.defaults.baseURL or construct the API base URL
          const apiBaseUrl = axios.defaults.baseURL || `${window.location.protocol}//${window.location.hostname}:8000`;
          processedHtml = processedHtml.replace(
            /src="\/uploads\/([^"]*)"/g,
            `src="${apiBaseUrl}/uploads/$1"`
          );
        }
        setHtmlContent(processedHtml);
      })
      .catch(err => {
        console.error('Error fetching purchase order:', err);
        setError('No se pudo cargar la orden de compra.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrdenCompra();
  }, [id]);

  const handleApproveSupervision = async () => {
    try {
      await axios.post(`/api/ordenes_compra/${id}/approve/supervision`);
      fetchOrdenCompra(); // Refresh data to show new status and signature
      setOpenSupervisionDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al aprobar con firma de Supervisión.');
      console.error(err);
    }
  };

  const handleApproveDireccionGeneral = async () => {
    try {
      await axios.post(`/api/ordenes_compra/${id}/approve/direccion_general`);
      fetchOrdenCompra(); // Refresh data to show new status and signature
      setOpenDireccionGeneralDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al aprobar con firma de Dirección General.');
      console.error(err);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.put(`/api/ordenes_compra/${id}/status`, {
        status: 'cancelada',
      });
      navigate(`/solicitudes_cotizacion/${ordenCompra.solicitud_cotizacion_id}`);
    } catch (err) {
      setError('No se pudo cancelar la orden de compra.');
      console.error(err);
    }
  };

  const handleFinish = async () => {
    try {
      await axios.put(`/api/ordenes_compra/${id}/status`, {
        status: 'terminada',
      });
      navigate(`/solicitudes_cotizacion/${ordenCompra.solicitud_cotizacion_id}`);
    } catch (err) {
      setError('No se pudo terminar la orden de compra.');
      console.error(err);
    }
  };

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };
  
  const handleOpenFinishDialog = () => {
    setOpenFinishDialog(true);
  };

  const handleCloseFinishDialog = () => {
    setOpenFinishDialog(false);
  };

  const handleOpenSupervisionDialog = () => {
    setOpenSupervisionDialog(true);
  };

  const handleCloseSupervisionDialog = () => {
    setOpenSupervisionDialog(false);
  };

  const handleOpenDireccionGeneralDialog = () => {
    setOpenDireccionGeneralDialog(true);
  };

  const handleCloseDireccionGeneralDialog = () => {
    setOpenDireccionGeneralDialog(false);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (!ordenCompra) return <Typography align="center">No se encontró la orden de compra.</Typography>;

  const canApprove = user && (user.role === 'ADMIN' || user.role === 'SUPERADMIN');
  const isActionable = ordenCompra.status_ciclo !== 'cancelado' && ordenCompra.status_ciclo !== 'terminado';

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <FamilyTree oc={ordenCompra} />

      <Grid container spacing={3}>
        {/* Main Content (Left) */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Previsualización del Documento" />
            <CardContent>
              <iframe 
                srcDoc={htmlContent}
                title={`OC Preview ${id}`}
                width="100%"
                height="800px"
                style={{ border: '1px solid #ccc' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar (Right) */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3} direction="column">
            {/* Summary Card */}
            <Grid item>
              <Card>
                <CardHeader title="Resumen" />
                <CardContent>
                  <Typography variant="body2" gutterBottom><strong>Empresa:</strong> {ordenCompra.empresa?.nombre}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Proveedor:</strong> {ordenCompra.proveedor?.nombre}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Fecha:</strong> {formatDate(ordenCompra.created_at, true)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}><strong>Estado:</strong></Typography>
                    <StatusChip status={ordenCompra.status} cicloStatus={ordenCompra.status_ciclo} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Actions Card */}
            <Grid item>
              <Card>
                <CardHeader title="Acciones" />
                <CardContent>
                  <Button fullWidth variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/ordenes-compra/${id}/edit`)} sx={{ mb: 2 }} disabled={!isActionable}>
                    Editar Orden de Compra
                  </Button>
                  {canApprove && ordenCompra.status !== 'Aprobada' && isActionable && (
                    <>
                      {!ordenCompra.url_firma_supervision && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleOpenSupervisionDialog}
                          sx={{ mb: 1 }}
                        >
                          Firmar Supervisión
                        </Button>
                      )}
                      {!ordenCompra.url_firma_direccion_general && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="secondary"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleOpenDireccionGeneralDialog}
                          sx={{ mb: 1 }}
                        >
                          Firmar Dirección General
                        </Button>
                      )}
                    </>
                  )}
                  {isActionable && (
                    <>
                      <Button fullWidth variant="contained" onClick={handleOpenCancelDialog} sx={{ mt: 2, backgroundColor: 'black', color: 'white', '&:hover': { backgroundColor: 'grey.800' } }}>
                        Cancelar
                      </Button>
                      <Button fullWidth variant="contained" onClick={handleOpenFinishDialog} sx={{ mt: 2, backgroundColor: '#FFC107', color: 'black', '&:hover': { backgroundColor: '#FFB300' } }}>
                        Marcar como Terminada
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
      >
        <DialogTitle>Cancelar Orden de Compra</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea cancelar (eliminar) este elemento?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { handleCancel(); handleCloseCancelDialog(); }} color="error">
            Sí, cancelar
          </Button>
          <Button onClick={handleCloseCancelDialog}>No</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={openFinishDialog}
        onClose={handleCloseFinishDialog}
      >
        <DialogTitle>Marcar como Terminada</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea marcar esta orden de compra como terminada? Esto también marcará la requisición y RFQ como terminadas, y cancelará otras RFQs no ganadoras.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { handleFinish(); handleCloseFinishDialog(); }} color="primary">
            Sí, terminar
          </Button>
          <Button onClick={handleCloseFinishDialog}>No</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSupervisionDialog}
        onClose={handleCloseSupervisionDialog}
      >
        <DialogTitle>Firmar con Supervisión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea firmar esta orden de compra con su firma de Supervisión?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveSupervision} color="primary">
            Sí, firmar
          </Button>
          <Button onClick={handleCloseSupervisionDialog}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDireccionGeneralDialog}
        onClose={handleCloseDireccionGeneralDialog}
      >
        <DialogTitle>Firmar con Dirección General</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea firmar esta orden de compra con su firma de Dirección General?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveDireccionGeneral} color="secondary">
            Sí, firmar
          </Button>
          <Button onClick={handleCloseDireccionGeneralDialog}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Cycle Side Panel */}
      <CycleSidePanel isOpen={sidePanelOpen} onToggle={() => setSidePanelOpen(!sidePanelOpen)}>
        <CycleNotesModule elementType="orden_compra" elementId={id} />
        <Divider sx={{ my: 3 }} />
        <CycleFilesModule elementType="orden_compra" elementId={id} />
      </CycleSidePanel>
    </Container>
  );
};

export default OrdenCompraDetail;
